/**
 * Admin V3 — Drivers — Compliance State Panel (G11.F)
 *
 * Operational requirement: the administrator must clearly understand
 * WHY a driver remains in their current compliance_state and what is
 * required to advance.
 *
 * Renders:
 *   - Current compliance_state (pill) + explainer sentence
 *   - Required documents checklist with per-type status:
 *       missing | submitted | verified | rejected | expired
 *
 * Data sources (already fetched by parent):
 *   - driver.compliance_state, driver.lifecycle_state
 *   - GET /admin/drivers/{id}/documents (provides per-doc statuses)
 *
 * Pure presentation — no new endpoints, no new fetches; data flow is
 * driven by the parent (DriverDetailPage).
 */

import React, { useMemo } from 'react';
import { CheckCircle2, AlertCircle, Clock, XCircle, ShieldAlert, FileText } from 'lucide-react';
import { SectionCard, complianceStateMeta } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { t } from '../../i18n';

// 8 French VTC document types per Plan §2.3.B + identity_document (ratified
// compliance-approval remediation, owner decision).
const REQUIRED_DOC_TYPES = [
  'permis_de_conduire',
  'carte_grise',
  'attestation_assurance',
  'capacite_professionnelle',
  'kbis',
  'casier_judiciaire',
  'justificatif_domicile',
  'identity_document',
];

const STATUS_ICON = {
  verified:  { Icon: CheckCircle2, className: 'text-emerald-600' },
  present:   { Icon: Clock,        className: 'text-sky-600' },
  rejected:  { Icon: XCircle,      className: 'text-rose-600' },
  expired:   { Icon: AlertCircle,  className: 'text-amber-600' },
  missing:   { Icon: AlertCircle,  className: 'text-slate-400' },
};

const DriverComplianceStatePanel = ({ driver, locale = 'fr' }) => {
  // Reuse the per-driver Documents endpoint that the Documents tab uses.
  // No new backend round-trip — the same data feeds both panels.
  const driverId = driver && driver.id;
  const { data: docsRaw } = useAdminQuery(
    driverId ? `/admin/drivers/${driverId}/documents` : null,
    {},
  );
  const docs = Array.isArray(docsRaw) ? docsRaw : [];

  const state = (driver && driver.compliance_state) || (driver && driver.lifecycle_state);
  const stateMeta = state ? complianceStateMeta[state] : null;
  const explainerKey = state ? `compliance.statePanel.explainer.${state}` : null;

  // Compute status per required doc type:
  // - If any doc with that type is `verified` → `verified`
  // - else if any is `pending` → `present`
  // - else if any is `rejected` → `rejected`
  // - else if any is `expired` (or has expiry_date in the past) → `expired`
  // - else → `missing`
  const docStatusByType = useMemo(() => {
    const now = new Date().toISOString();
    const out = {};
    for (const type of REQUIRED_DOC_TYPES) {
      const list = docs.filter((d) => d.document_type === type);
      let s = 'missing';
      if (list.some((d) => d.verification_status === 'verified')) s = 'verified';
      else if (list.some((d) => d.verification_status === 'pending')) s = 'present';
      else if (list.some((d) => d.verification_status === 'rejected')) s = 'rejected';
      else if (list.some((d) => (d.expiry_date && d.expiry_date < now) || d.verification_status === 'expired')) s = 'expired';
      out[type] = s;
    }
    return out;
  }, [docs]);

  return (
    <SectionCard
      icon={ShieldAlert}
      iconClass="bg-indigo-100 text-indigo-700"
      title={t('compliance.statePanel.title', locale)}
    >
      <div className="space-y-4" data-testid="driver-compliance-state-panel">
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">
            {t('compliance.statePanel.current', locale)}
          </span>
          {stateMeta ? (
            <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${stateMeta.pill}`}>
              {t(stateMeta.labelKey, locale)}
            </span>
          ) : (
            <span className="text-slate-600 text-xs">{state || '—'}</span>
          )}
        </div>

        {explainerKey && (
          <p className="text-sm text-slate-600">
            {t(explainerKey, locale)}
          </p>
        )}

        <div>
          <p className="text-sm font-medium text-slate-700 mb-2">
            {t('compliance.statePanel.required.title', locale)}
          </p>
          <ul className="space-y-1.5" data-testid="state-panel-required-docs">
            {REQUIRED_DOC_TYPES.map((type) => {
              const status = docStatusByType[type] || 'missing';
              const icon = STATUS_ICON[status] || STATUS_ICON.missing;
              const { Icon, className } = icon;
              return (
                <li
                  key={type}
                  className="flex items-center gap-2 text-sm"
                  data-testid={`state-panel-doc-${type}`}
                >
                  <Icon className={`h-4 w-4 ${className}`} />
                  <span className="text-slate-700">
                    {t(`compliance.doctype.${type}`, locale)}
                  </span>
                  <span className={`text-xs ${className}`}>
                    — {t(`compliance.statePanel.required.status.${status}`, locale)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </SectionCard>
  );
};

export default DriverComplianceStatePanel;
